import { ProjectIdRequest } from 'api/dtos/general/request';
import {
  ChangeProjectSettingsRequest,
  CreateProjectRequest,
  GetProjectUtilizationByDtoRequest,
  ISetProjectGeneralSettingsRequest,
  SetConfidenceLevelRequest,
  SetLawZoneRequest,
} from 'api/dtos/project/request';
import {
  CreateProjectResponse,
  GetProjectResponse,
  IProjectSia416Areas,
  IProjectVolumes,
} from 'api/dtos/project/response';
import { backendApi } from 'state/services/backend/api';
import { EBackendTag } from 'state/services/backend/types';
import { IProjectUtilization } from 'types/project/utilization';
import { RequestMethod } from 'types/request';

export const projectApiSlice = backendApi.injectEndpoints({
  endpoints: (builder) => ({
    createProject: builder.mutation<CreateProjectResponse, CreateProjectRequest>({
      query: (body) => ({
        url: `/api/project`,
        method: RequestMethod.POST,
        body,
      }),
      invalidatesTags: [EBackendTag.PROJECT, EBackendTag.CREDITS],
    }),
    getProject: builder.query<GetProjectResponse, ProjectIdRequest>({
      query: ({ projectId }) => ({
        url: `/api/project/${projectId}`,
        method: RequestMethod.GET,
      }),
      providesTags: [EBackendTag.PROJECT],
    }),
    changeProjectSettings: builder.mutation<any, ChangeProjectSettingsRequest>({
      query: ({ projectId, name, description }) => ({
        url: `/api/project/${projectId}/change-settings`,
        method: RequestMethod.PUT,
        body: { name, description },
      }),
      invalidatesTags: [EBackendTag.PROJECT],
    }),
    getProjectSiaAreas: builder.query<IProjectSia416Areas, ProjectIdRequest>({
      query: ({ projectId }) => ({
        url: `/api/project-areas/${projectId}/sia416`,
        method: RequestMethod.GET,
      }),
      providesTags: [EBackendTag.PROJECT_AREAS],
    }),
    getProjectVolumes: builder.query<IProjectVolumes, ProjectIdRequest>({
      query: ({ projectId }) => ({
        url: `/api/project/${projectId}/volumes`,
        method: RequestMethod.GET,
      }),
      providesTags: [EBackendTag.PROJECT_VOLUMES],
    }),
    getProjectUtilization: builder.query<IProjectUtilization, ProjectIdRequest>({
      query: ({ projectId }) => ({
        url: `/api/project-utilization/${projectId}`,
        method: RequestMethod.GET,
      }),
      providesTags: [EBackendTag.UTILIZATION],
    }),
    getProjectUtilizationByDTO: builder.query<IProjectUtilization, GetProjectUtilizationByDtoRequest>({
      query: ({ projectId, buildings, existingBuildings }) => ({
        url: `/api/project-utilization/${projectId}/by-dto`,
        method: RequestMethod.POST,
        body: { buildings, existingBuildings },
      }),
    }),
    setConfidenceLevel: builder.mutation<void, SetConfidenceLevelRequest>({
      query: ({ projectId, confidenceLevel }) => ({
        url: `/api/project/${projectId}/confidence-level/${confidenceLevel}`,
        method: RequestMethod.PUT,
      }),
      invalidatesTags: [EBackendTag.PROJECT, EBackendTag.ENERGY],
    }),
    setLawZone: builder.mutation<void, SetLawZoneRequest>({
      query: ({ projectId, zoneId }) => ({
        url: `/api/project/${projectId}/law-zone/${zoneId}`,
        method: RequestMethod.PUT,
      }),
      invalidatesTags: [
        EBackendTag.PROJECT,
        EBackendTag.PROJECT_RULES_CHECK,
        EBackendTag.USER_PROJECT_RULES,
        EBackendTag.DATA_OVERVIEW,
      ],
    }),
    setProjectGeneralSettings: builder.mutation<void, ISetProjectGeneralSettingsRequest>({
      query: ({ projectId, body }) => ({
        url: `/api/project/${projectId}/general-settings`,
        method: RequestMethod.POST,
        body,
      }),
    }),
  }),
});

export const {
  useCreateProjectMutation,
  useGetProjectQuery,
  useChangeProjectSettingsMutation,
  useGetProjectSiaAreasQuery,
  useGetProjectUtilizationQuery,
  useGetProjectUtilizationByDTOQuery,
  useGetProjectVolumesQuery,
  useSetConfidenceLevelMutation,
  useSetLawZoneMutation,
} = projectApiSlice;

export const useGetProjectQueryState = projectApiSlice.endpoints.getProject.useQueryState;

export const useGetProjectSiaAreasQueryState = projectApiSlice.endpoints.getProjectSiaAreas.useQueryState;

export const useGetProjectVolumesQueryState = projectApiSlice.endpoints.getProjectVolumes.useQueryState;
